// ─────────────────────────────────────────────────────────────────────────────
// useOarlockCalibration
// Loads the user's saved oarlock configs for the current boat and exposes a
// calibrate() helper plus reactive state. Subscribes to a forward-compat
// `crewz-gate-metrics` window event so the moment CrewZ smart-gate firmware
// starts streaming, calibrated force/work/power flow into the rest of the app
// without further wiring.
//
// Wire shape (when hardware ships):
//   window.dispatchEvent(new CustomEvent('crewz-gate-metrics', {
//     detail: {
//       side: 'port' | 'starboard' | 'sweep' | 'single_scull',
//       force_n: number,        // raw force, Newtons
//       work_j: number,         // raw work, Joules per stroke
//       power_w: number,        // raw power, Watts
//       angle_deg: number,      // catch/finish angle, degrees
//       stroke_count: number,
//       timestamp: number,      // optional, ms epoch
//     }
//   }));
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCULLING_BOATS = new Set(['1x', '2x', '4x', 'c2_erg']);

const isSculling = (boatType) => boatType && SCULLING_BOATS.has(boatType);

/**
 * Apply a single oarlock config to one raw gate reading.
 * Subtracts zero-force / zero-angle offsets and applies the MFW2 doubler.
 */
export function calibrateReading(raw, config) {
  if (!raw || !config) return null;
  const zeroF = Number(config.set_zero_force) || 0;
  const zeroA = Number(config.set_zero_angle) || 0;
  const mult = config.multiply_fwp_by_2 ? 2 : 1;

  const force = Math.max(0, (Number(raw.force_n) || 0) - zeroF) * mult;
  const work = Math.max(0, (Number(raw.work_j) || 0)) * mult;
  const power = Math.max(0, (Number(raw.power_w) || 0)) * mult;
  const angle = (Number(raw.angle_deg) || 0) - zeroA;

  return {
    side: config.side,
    force_n: force,
    work_j: work,
    power_w: power,
    angle_deg: angle,
    stroke_count: Number(raw.stroke_count) || 0,
    timestamp: raw.timestamp || Date.now(),
    config_id: config.config_id,
  };
}

/**
 * Pick the configs that match the current rig.
 *  - Sweep boats: side === 'sweep'
 *  - Sculling rigs (two oarlocks): port + starboard
 *  - Sculling rigs (single-oarlock toggle): single_scull only
 */
function selectActiveConfigs(allConfigs, boatType, singleSculling) {
  if (!Array.isArray(allConfigs)) return [];
  const sculling = isSculling(boatType);
  if (!sculling) return allConfigs.filter(c => c.side === 'sweep');
  if (singleSculling) return allConfigs.filter(c => c.side === 'single_scull');
  return allConfigs.filter(c => c.side === 'port' || c.side === 'starboard');
}

export default function useOarlockCalibration({ vendor = 'nk_empower', boatType, singleSculling } = {}) {
  const [allConfigs, setAllConfigs] = useState([]);
  const [latestBySide, setLatestBySide] = useState({});
  const [aggregate, setAggregate] = useState({ force_n: 0, work_j: 0, power_w: 0 });
  const [streaming, setStreaming] = useState(false);
  const streamingTimeoutRef = useRef(null);

  // Load configs (the GET /api/oarlocks endpoint is auth-gated; silent on failure).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/oarlocks?vendor=${vendor}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (!cancelled) setAllConfigs(list);
      } catch (e) {
        if (!cancelled) setAllConfigs([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [vendor]);

  const activeConfigs = useMemo(
    () => selectActiveConfigs(allConfigs, boatType, singleSculling),
    [allConfigs, boatType, singleSculling]
  );

  /**
   * Calibrate a raw gate reading. If no saved config matches this side we
   * fall back to a zero-offset passthrough so the UI still shows live values
   * (otherwise Peak Force / Catch Angle would stay blank for any user who
   * connected a gate without running the calibration wizard yet).
   */
  const calibrate = useCallback((raw) => {
    if (!raw || !raw.side) return null;
    const cfg = activeConfigs.find(c => c.side === raw.side);
    if (cfg) return calibrateReading(raw, cfg);
    // Passthrough — same shape as calibrateReading() but with no zeroing.
    return {
      side: raw.side,
      force_n: Math.max(0, Number(raw.force_n) || 0),
      work_j: Math.max(0, Number(raw.work_j) || 0),
      power_w: Math.max(0, Number(raw.power_w) || 0),
      angle_deg: Number(raw.angle_deg) || 0,
      stroke_count: Number(raw.stroke_count) || 0,
      timestamp: raw.timestamp || Date.now(),
      config_id: null,
      uncalibrated: true,
    };
  }, [activeConfigs]);

  // Subscribe to forward-compat window event from the CrewZ BLE service.
  useEffect(() => {
    const onGate = (event) => {
      const calibrated = calibrate(event.detail);
      if (!calibrated) return;
      setLatestBySide(prev => ({ ...prev, [calibrated.side]: calibrated }));
      setStreaming(true);
      if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
      // Mark stream stale 4s after the last reading
      streamingTimeoutRef.current = setTimeout(() => setStreaming(false), 4000);
    };
    window.addEventListener('crewz-gate-metrics', onGate);
    return () => {
      window.removeEventListener('crewz-gate-metrics', onGate);
      if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
    };
  }, [calibrate]);

  // Aggregate readings across active sides — sum for sweep / single_scull
  // (already doubled by MFW2), or sum port + starboard on a two-gate sculler.
  useEffect(() => {
    const sides = activeConfigs.map(c => c.side);
    if (sides.length === 0) {
      setAggregate({ force_n: 0, work_j: 0, power_w: 0 });
      return;
    }
    const sum = { force_n: 0, work_j: 0, power_w: 0 };
    sides.forEach(s => {
      const r = latestBySide[s];
      if (!r) return;
      sum.force_n += r.force_n;
      sum.work_j += r.work_j;
      sum.power_w += r.power_w;
    });
    setAggregate(sum);
  }, [latestBySide, activeConfigs]);

  return {
    /** Raw configs for this vendor (all rigs the user has saved). */
    allConfigs,
    /** Configs currently driving calibration (depend on boat + single-scull toggle). */
    activeConfigs,
    /** True while gate readings have arrived in the last 4 seconds. */
    streaming,
    /** Most recent calibrated reading per side. */
    latestBySide,
    /** Summed force/work/power across active oarlocks (already MFW2-adjusted). */
    aggregate,
    /** Pure helper — apply the active config to a raw reading. */
    calibrate,
    /** Convenience: true if the user has at least one config for the current rig. */
    hasActiveConfig: activeConfigs.length > 0,
  };
}
